import Settings from "@builder/components/app/Settings";
import StoreData from "@builder/components/app/Settings/StoreData";
import { setRightInstance } from "@builder/store/slices/sidebarSlice";
import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import StructureTab from "../StructureTab";
import "./style.scss";
import OliverText from "@builder/components/general/OliverText";
import TooltipElement from "@builder/components/general/Tooltip";
import { useTranslation } from "react-i18next";

const MIN_WIDTH = 283;
const MAX_WIDTH = 1200;
const COLLAPSED_WIDTH = 20;

const OliverRightSidebar: React.FC = () => {
    const sidebarRef = useRef<HTMLDivElement>(null);
    const [isResizing, setIsResizing] = useState(false);
    const [rightSidebarWidth, setRightSidebarWidth] = useState(MIN_WIDTH);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    // Right sidebar instance in Redux becasue I need to modify it from other components
    const rightSidebarInstance = useAppSelector(state => state.sidebar.rightSidebar.instance);

    const changeCategory = (category: "structure" | "storeData" | "settings") => {
        dispatch(setRightInstance(category));
    };

    const handleMouseDown = () => {
        setIsResizing(true);
    };

    const handleMouseUp = () => {
        setIsResizing(false);
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isResizing || !sidebarRef.current || isCollapsed) return;
        const newWidth = sidebarRef.current.offsetWidth - (e.clientX - sidebarRef.current.getBoundingClientRect().left);
        console.log(newWidth);

        if (newWidth < COLLAPSED_WIDTH && !isCollapsed) {
            setRightSidebarWidth(COLLAPSED_WIDTH);
            setIsCollapsed(true);
            return;
        }
        if (newWidth < MIN_WIDTH || newWidth > MAX_WIDTH) return;
        setRightSidebarWidth(newWidth);
    };

    const toggleCollapse = () => {
        if (isCollapsed) {
            setRightSidebarWidth(MIN_WIDTH);
            setIsCollapsed(false);
        } else {
            setIsResizing(false);
            setRightSidebarWidth(COLLAPSED_WIDTH);
            setIsCollapsed(true);
        }
    };

    useEffect(() => {
        if (sidebarRef.current) {
            sidebarRef.current.style.width = `${rightSidebarWidth}px`;
        }

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);

        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isResizing, rightSidebarWidth]);

    if (isCollapsed) {
        return (
            <div className="oliver-right-sidebar-collapsed transition" ref={sidebarRef}>
                <div className="open-arrow" onClick={toggleCollapse}>
                    <img src="/angled-arrow-down-dark.svg" alt="" />
                </div>
            </div>
        );
    }

    return (
        <div className={`oliver-right-sidebar ${isResizing ? "" : "transition"}`} ref={sidebarRef}>
            <div className="sidebar-tabs">
                <TooltipElement text={t("tooltip.structure")} position="bottom" extraStyle={{ width: "200px" }}>
                    <div className={rightSidebarInstance === "structure" ? "tab active" : "tab"} onClick={() => changeCategory("structure")}>
                        <OliverText text="Structure" size="xs" translatePrefix="sidebarCollapse." color="base-grey" />
                    </div>
                </TooltipElement>
                <TooltipElement text={t("tooltip.storeData")} position="bottom" extraStyle={{ width: "200px" }}>
                    <div className={rightSidebarInstance === "storeData" ? "tab active" : "tab"} onClick={() => changeCategory("storeData")}>
                        <OliverText text="Store Data" size="xs" translatePrefix="sidebarCollapse." color="base-grey" />
                    </div>
                </TooltipElement>
                <TooltipElement text={t("tooltip.settings")} position="bottom" extraStyle={{ width: "200px" }}>
                    <div className={rightSidebarInstance === "settings" ? "tab active" : "tab"} onClick={() => changeCategory("settings")}>
                        <OliverText text="Settings" size="xs" translatePrefix="sidebarCollapse." color="base-grey" />
                    </div>
                </TooltipElement>
            </div>

            {rightSidebarInstance === "storeData" ? <StoreData /> : rightSidebarInstance === "settings" ? <Settings /> : <StructureTab />}
            <div className="resize-gripper" onMouseDown={handleMouseDown} />
        </div>
    );
};

export default OliverRightSidebar;
